<template>
  <div class="message-container" :class="type === 'ai' ? 'not-user-message' : 'user-message'">
    <div class="message-content" :class="type === 'ai' ? 'not-user-content' : 'user-content'">
      <!-- Sender label -->
      <div class="message-header" :class="type === 'ai' ? 'not-user-header' : 'user-header'">
        <span class="sender-name">{{ type === 'ai' ? 'AI Assistant:' : 'Me' }}</span>
      </div>

      <!-- Message bubble -->
      <div class="message-bubble relative">
        <!-- Error state -->
        <div v-if="error" class="bubble-content error-bubble">
          <div class="absolute top-[8px] left-[2px]">
            <img src="@/assets/MyPatient/message_arrow2.svg" alt="Arrow" class="w-[18px] h-[18px]"/>
          </div>
          <div class="error-content">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" class="error-icon">
              <circle cx="10" cy="10" r="9" fill="#EF4444"/>
              <path d="M10 6V11" stroke="white" stroke-width="2" stroke-linecap="round"/>
              <circle cx="10" cy="14" r="1" fill="white"/>
            </svg>
            <div class="error-text-wrapper">
              <p class="error-message">{{ content }}</p>
              <div v-if="showAction" class="error-actions">
                <a href="#" class="error-action-link" @click.prevent="$emit('action-click')">Action</a>
                <span class="error-more-info">more info</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Thinking state (three dots animation) -->
        <div v-else-if="loading && loadingState === 'thinking'" class="bubble-content not-user-bubble">
          <div class="absolute top-[8px] -left-[6px]">
            <img :src="require('@/assets/MyPatient/message_arrow1.svg')" alt="Arrow" class="w-[18px] h-[18px]"/>
          </div>
          <p class="loading-text">Thinking</p>
          <div class="thinking-dots">
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </div>
        </div>

        <!-- Generating state (three dots animation) -->
        <div v-else-if="loading && loadingState === 'generating'" class="bubble-content not-user-bubble">
          <div class="absolute top-[8px] -left-[6px]">
            <img :src="require('@/assets/MyPatient/message_arrow1.svg')" alt="Arrow" class="w-[18px] h-[18px]"/>
          </div>
          <p class="loading-text">Generating</p>
          <div class="thinking-dots">
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </div>
        </div>

        <!-- Legacy loading state (for backward compatibility) -->
        <div v-else-if="loading" class="bubble-content not-user-bubble">
          <div class="absolute top-[8px] -left-[6px]">
            <img :src="require('@/assets/MyPatient/message_arrow1.svg')" alt="Arrow" class="w-[18px] h-[18px]"/>
          </div>
          <p class="loading-text">{{ content || 'Generating workflow...' }}</p>
        </div>

        <!-- Normal message -->
        <div v-else class="bubble-content" :class="type === 'ai' ? 'not-user-bubble' : 'user-bubble'">
          <div class="absolute" :class="type === 'ai' ? 'top-[8px] -left-[6px]' : 'top-[8px] -right-[6px]'">
            <img :src="type === 'ai' ? require('@/assets/MyPatient/message_arrow1.svg') : require('@/assets/MyPatient/message_arrow2.svg')" alt="Arrow" class="w-[18px] h-[18px]"/>
          </div>
          {{ content }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AIMessageBubble',
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ['ai', 'user'].includes(value)
    },
    content: {
      type: String,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingState: {
      type: String,
      default: null,
      validator: (value) => value === null || ['thinking', 'generating'].includes(value)
    },
    error: {
      type: Boolean,
      default: false
    },
    showAction: {
      type: Boolean,
      default: false
    }
  }
};
</script>

<style scoped>
.message-container {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  max-width: 100%;
}

/* User messages (right side) */
.user-message {
  flex-direction: row-reverse;
}

/* AI messages (left side) */
.not-user-message {
  flex-direction: row;
}

.message-content {
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

.user-content {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.not-user-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.message-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.user-header {
  flex-direction: row-reverse;
}

.not-user-header {
  flex-direction: row;
}

.sender-name {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 11px;
  line-height: 16px;
  letter-spacing: 0.5px;
  color: #333333;
}

.message-bubble {
  position: relative;
  max-width: 100%;
  word-wrap: break-word;
}

.bubble-content {
  padding: 12px 24px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  position: relative;
  color: #333333;
  border-radius: 20px;
}

/* User message bubble styling */
.user-bubble {
  background-color: #F2F2F2;
  margin-right: 10px;
}

/* AI message bubble styling */
.not-user-bubble {
  background-color: #E4F3F5;
  margin-left: 10px;
}

/* Error bubble */
.error-bubble {
  background-color: #FEE2E2;
  padding: 12px;
}

.error-content {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.error-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.error-text-wrapper {
  flex: 1;
}

.error-message {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #EF4444;
  margin: 0 0 4px 0;
}

.error-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.error-action-link {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #004879;
  text-decoration: underline;
  cursor: pointer;
}

.error-action-link:hover {
  color: #003366;
}

.error-more-info {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #666;
}

/* Loading state */
.loading-text {
  font-style: italic;
  color: #666;
  margin: 0;
  display: inline-block;
  margin-right: 8px;
}

/* Three dot animation for thinking/generating */
.thinking-dots {
  display: inline-flex;
  gap: 4px;
  align-items: center;
}

.thinking-dots .dot {
  width: 6px;
  height: 6px;
  background-color: #666;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.thinking-dots .dot:nth-child(1) {
  animation-delay: -0.32s;
}

.thinking-dots .dot:nth-child(2) {
  animation-delay: -0.16s;
}

.thinking-dots .dot:nth-child(3) {
  animation-delay: 0s;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
